import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil } from "lucide-react";

const ProductModal = ({
  open,
  onOpenChange,
  API,
  currentUser,
  editProduct,
  productLabels,
  productForm,
  setProductForm,
  stockSuggestions,
  setStockSuggestions,
  onOpenLabelsEditor,
  onSave,
}) => {
  const handleNameChange = (e) => {
    const v = e.target.value;
    setProductForm({ ...productForm, name: v });
    // Fetch stock suggestions on the fly (debounced)
    if (!editProduct && v && v.trim().length >= 2 && !productForm.stock_product_id) {
      if (window._stockSuggestTimer) clearTimeout(window._stockSuggestTimer);
      window._stockSuggestTimer = setTimeout(async () => {
        try {
          const r = await axios.get(`${API}/caisse/products/stock-suggestions`, {
            params: { name: v, limit: 5 },
          });
          setStockSuggestions(r.data?.suggestions || []);
        } catch (err) {
          setStockSuggestions([]);
        }
      }, 250);
    } else {
      setStockSuggestions([]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white" data-testid="product-modal">
        <DialogHeader>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <DialogTitle data-testid="product-modal-title">
                {editProduct ? productLabels.title_edit : productLabels.title_create}
              </DialogTitle>
              {productLabels.description ? (
                <p className="text-xs text-slate-400 mt-1" data-testid="product-modal-description">
                  {productLabels.description}
                </p>
              ) : null}
            </div>
            {currentUser?.role === "admin" && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onOpenLabelsEditor}
                className="h-7 px-2 text-amber-300 hover:bg-amber-500/10 flex-shrink-0"
                title="Modifier les libellés (Admin)"
                data-testid="open-labels-editor-btn"
              >
                <Pencil className="w-3.5 h-3.5 mr-1" />
                <span className="text-[11px]">Libellés</span>
              </Button>
            )}
          </div>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Nom</Label>
            <Input
              value={productForm.name}
              onChange={handleNameChange}
              onBlur={() => setTimeout(() => setStockSuggestions([]), 200)}
              className="bg-slate-700 border-slate-600"
              data-testid="product-name-input"
            />
            {!editProduct && stockSuggestions.length > 0 && !productForm.stock_product_id && (
              <div className="bg-slate-900/80 border border-emerald-500/30 rounded p-2 space-y-1" data-testid="stock-suggestions-panel">
                <div className="text-[11px] text-emerald-300 font-semibold">
                  💡 Lier ce produit à un produit Stock existant ?
                </div>
                {stockSuggestions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      setProductForm({ ...productForm, stock_product_id: s.id, stock_product_name: s.name });
                      setStockSuggestions([]);
                      toast.success(`Produit lié à : ${s.name}`);
                    }}
                    className="w-full text-left px-2 py-1 rounded bg-slate-800 hover:bg-emerald-900/40 text-xs text-white flex justify-between items-center"
                    data-testid={`suggest-stock-${s.id}`}
                  >
                    <span>📦 {s.name} <span className="text-slate-400">({s.unit}, qté {s.quantity})</span></span>
                    <span className="text-emerald-400">{Math.round(s.score * 100)}%</span>
                  </button>
                ))}
              </div>
            )}
            {productForm.stock_product_id && (
              <div className="text-xs text-emerald-300 flex items-center gap-2">
                🔗 Lié au stock : {productForm.stock_product_name || productForm.stock_product_id}
                <button
                  type="button"
                  onClick={() => setProductForm({ ...productForm, stock_product_id: null, stock_product_name: null })}
                  className="text-rose-400 hover:underline text-[11px]"
                >
                  (délier)
                </button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Prix (FCFA)</Label>
              <Input
                type="number"
                value={productForm.price}
                onChange={(e) => setProductForm({ ...productForm, price: parseFloat(e.target.value) || 0 })}
                className="bg-slate-700 border-slate-600"
                data-testid="product-price-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Unité</Label>
              <Input
                value={productForm.unit}
                onChange={(e) => setProductForm({ ...productForm, unit: e.target.value })}
                className="bg-slate-700 border-slate-600"
                data-testid="product-unit-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Categorie (carte menu)</Label>
            <Select
              value={productForm.category || "none"}
              onValueChange={(v) => setProductForm({ ...productForm, category: v === "none" ? "" : v })}
            >
              <SelectTrigger className="bg-slate-700 border-slate-600" data-testid="product-category-trigger">
                <SelectValue placeholder="Selectionner une categorie" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="none">-- Aucune --</SelectItem>
                <SelectItem value="Plats">Plats</SelectItem>
                <SelectItem value="Entrees">Entrees</SelectItem>
                <SelectItem value="Grillades">Grillades</SelectItem>
                <SelectItem value="Sauces">Sauces</SelectItem>
                <SelectItem value="Accompagnements">Accompagnements</SelectItem>
                <SelectItem value="Boissons">Boissons</SelectItem>
                <SelectItem value="Cocktails">Cocktails</SelectItem>
                <SelectItem value="Desserts">Desserts</SelectItem>
                <SelectItem value="Petit-dejeuner">Petit-dejeuner</SelectItem>
                <SelectItem value="Snacks">Snacks</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={onSave} className="w-full bg-purple-500 hover:bg-purple-600" data-testid="product-save-btn">
            Enregistrer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductModal;
